import React, { useMemo, useCallback, useContext } from 'react';
import { Rate } from 'antd';
import type { RateProps as AntRateProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { mergeStoreProps } from '../../base/storePropsUtils';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';

/**
 * AndRate Props 接口
 */
export interface AndRateProps extends Omit<AntRateProps, 'onChange'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  /** 变更动作 */
  changeAction?: string;
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 自定义变更处理函数 */
  onChange?: (value: number) => void;
}

/**
 * AndRate 组件
 * 封装 Ant Design Rate，支持数据绑定和动作解析
 */
export const AndRate: React.FC<AndRateProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  onChange,
  value: propValue,
  defaultValue,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && dataSetField ? s.getDataSet(dataSetKey, dataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const displayValue = useMemo(() => {
    if (storeValue !== undefined) return storeValue;
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue ?? defaultValue;
  }, [storeValue, storeProps?.value, propValue, defaultValue]);

  const handleChange = useCallback(
    (value: number) => {
      if (onChange) {
        onChange(value);
        return;
      }
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, value);
      if (validateOnChange) validate(value);
      if (changeAction && sessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, value, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [onChange, dataSetKey, dataSetField, setDataSet, validateOnChange, validate, changeAction, sessionId, id, addCommand, ctx]
  );

  const handleBlur = useCallback(() => {
    if (validateOnBlur) validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  return (
    <div onBlur={handleBlur}>
      <Rate
        {...mergedProps}
        disabled={develop ? false : mergedProps.disabled}
        value={displayValue}
        onChange={handleChange}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndRate;
